import React from 'react'
import { Form, Row, Col } from 'antd'
import { FormikProps, FormikValues } from 'formik'
import { number, object, setLocale, string } from 'yup'
import { useSelector } from 'react-redux'
import moment from 'moment'
import { ApplicationState } from 'src/store'
import {
  DatePickerRange,
  Select,
  SelectParticipant,
  SelectResource
} from 'src/components/Formik'
import { stringify } from 'src/core/hooks/use-translation'
import SelectOrganizationCurrentManager from 'src/components/Formik/select-organization-current-manager'

const options = [
  { label: 'Cancelado', value: 'CANCELED' },
  { label: 'Emitido', value: 'ISSUED' },
  { label: 'Finalizado', value: 'FINISHED' },
  { label: 'Aprovação pendente', value: 'APPROVAL_PENDING' },
  { label: 'Aprovação reprovada', value: 'APPROVAL_REPROVED' },
  { label: 'Conferência pendente', value: 'CONFERENCE_PENDING' },
  { label: 'Conferência reprovada', value: 'CONFERENCE_REPROVED' },
  { label: 'Cotação pendente', value: 'QUOTATION_PENDING' },
  { label: 'Emissão cancelada', value: 'EMISSION_CANCELED' },
  { label: 'Emissão pendente', value: 'EMISSION_PENDING' },
  { label: 'Fechamento pendente', value: 'CLOSING_PENDING' },
  { label: 'Fechamento reprovado', value: 'CLOSING_REPROVED' }
]

function ConferenceRange({
  formikProps
}: {
  formikProps: FormikProps<FormikValues>
}) {
  const {
    me: {
      user: { tenant_id }
    }
  } = useSelector((state: ApplicationState) => ({
    me: state.me
  }))
  if (![597990758089, 559042084949].includes(+tenant_id)) return <></>

  return (
    <Row>
      <Col span={24}>
        <div>
          <DatePickerRange
            formikProps={formikProps}
            label="web.report.14.conference-period"
            name={['conference_started_on', 'conference_ended_on']}
            placeholder={[
              'web.reports.parameter.period.placeholder-start',
              'web.reports.parameter.period.placeholder-end'
            ]}
            required
            showTime={false}
          />
        </div>
      </Col>
    </Row>
  )
}

function TicketStatus({
  formikProps
}: {
  formikProps: FormikProps<FormikValues>
}) {
  const {
    me: {
      user: { tenant_id }
    }
  } = useSelector((state: ApplicationState) => ({
    me: state.me
  }))

  if (![597990758089, 559042084949].includes(+tenant_id)) return <></>

  return (
    <Row>
      <Col span={24}>
        <div>
          <Select
            label="common.status-filter"
            dataTestId="ticket_status"
            formikProps={formikProps}
            name="ticket_status"
            options={options}
          />
        </div>
      </Col>
    </Row>
  )
}

function getInitialValues() {
  return {
    billing_type_id: '',
    conference_ended_on: '',
    conference_started_on: '',
    contract_id: '',
    cost_center_id: '',
    ended_on: moment().utc().endOf('month').unix(),
    organization_id: '',
    payment_resource_id: '',
    reason_id: '',
    reference_id: '',
    service_id: '',
    started_on: moment().utc().startOf('month').unix(),
    target_id: '',
    ticket_status: '',
    traveler_id: ''
  }
}

function getRenderForm() {
  return (formikProps: FormikProps<FormikValues>) => (
    <Form onSubmit={formikProps.handleSubmit}>
      <Row>
        <Col span={24}>
          <SelectOrganizationCurrentManager
            formikProps={formikProps}
            required
          />
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <div>
            <DatePickerRange
              formikProps={formikProps}
              label="web.reports.parameter.period.title"
              name={['started_on', 'ended_on']}
              placeholder={[
                'web.reports.parameter.period.placeholder-start',
                'web.reports.parameter.period.placeholder-end'
              ]}
              required
              showTime={false}
            />
          </div>
        </Col>
      </Row>
      <ConferenceRange formikProps={formikProps} />
      <TicketStatus formikProps={formikProps} />
      <Row>
        <Col span={24}>
          <SelectResource
            defaultActiveFirstOption={false}
            formikProps={formikProps}
            keyLabel="name"
            keyValue="reason_id"
            label="web.policies-flow.type.reason"
            name="reason_id"
            placeholder="web.policies.rules.modal-title-reason"
            required={false}
            resource="reasons"
          />
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <SelectResource
            defaultActiveFirstOption={false}
            formikProps={formikProps}
            keyLabel="name"
            keyValue="service_id"
            label="web.receipt.form.service.title"
            name="service_id"
            placeholder="web.receipt.form.service.placeholder"
            required={false}
            resource="services"
          />
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <SelectResource
            defaultActiveFirstOption={false}
            formikProps={formikProps}
            keyLabel="name"
            keyValue="payment_resource_id"
            label="web.receipt.form.payment-resource.title"
            name="payment_resource_id"
            placeholder="web.receipt.form.payment-resource.placeholder"
            required={false}
            resource="payment_resources"
          />
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <SelectResource
            defaultActiveFirstOption={false}
            formikProps={formikProps}
            keyLabel="name"
            keyValue="cost_center_id"
            label="web.assignment.sharing.form.cost-center.title"
            name="cost_center_id"
            placeholder="web.assignment.sharing.form.cost-center.placeholder"
            required={false}
            resource="cost_centers"
          />
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <SelectResource
            defaultActiveFirstOption={false}
            formikProps={formikProps}
            keyLabel="name"
            keyValue="billing_type_id"
            label="web.assignment.billing.form.billing-type.title"
            name="billing_type_id"
            placeholder="web.assignment.billing.form.billing-type.placeholder"
            required={false}
            resource="billing_types"
          />
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <SelectResource
            defaultActiveFirstOption={false}
            formikProps={formikProps}
            keyLabel="name"
            keyValue="contract_id"
            label="web.assignment.sharing.form.contract.title"
            name="contract_id"
            placeholder="web.assignment.sharing.form.contract.placeholder"
            required={false}
            resource="contracts"
          />
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <SelectResource
            defaultActiveFirstOption={false}
            formikProps={formikProps}
            keyLabel="name"
            keyValue="reference_id"
            label="web.itinerary.form.reference.title"
            name="reference_id"
            placeholder="web.itinerary.form.reference.placeholder"
            required={false}
            resource="references"
          />
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <SelectResource
            defaultActiveFirstOption={false}
            formikProps={formikProps}
            keyLabel="name"
            keyValue="target_id"
            label="web.assignment.billing.form.target.title"
            name="target_id"
            placeholder="web.assignment.billing.form.target.placeholder"
            required={false}
            resource="targets"
          />
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <SelectParticipant
            defaultActiveFirstOption={false}
            formikProps={formikProps}
            label="web.reports.parameter.participant.title"
            name="traveler_id"
            placeholder="web.reports.parameter.participant.placeholder"
            required={false}
          />
        </Col>
      </Row>
    </Form>
  )
}

function getValidationSchema() {
  setLocale({
    mixed: {
      notType: 'common.required',
      required: 'common.required'
    }
  })
  return object().shape({
    ended_on: number()
      .when('started_on', (started_on: number, schema: any) =>
        schema.max(
          moment.unix(started_on).add(365, 'days').unix(),
          stringify('web.reports.parameter.period.range-error', { value: 365 })
        )
      )
      .required()
      .typeError(),
    organization_id: string().required(),
    conference_ended_on: number()
      .when('conference_started_on', (started_on: number, schema: any) =>
        schema.max(
          moment.unix(started_on).add(365, 'days').unix(),
          stringify('web.reports.parameter.period.range-error', { value: 365 })
        )
      )
      .notRequired()
      .typeError(),
    conference_started_on: number().notRequired().typeError(),
    started_on: number().required().typeError()
  })
}

export { getInitialValues, getRenderForm, getValidationSchema }
